import { VerifyBanner } from '@/components/verify-banner'
import { createFileRoute, Link } from '@tanstack/react-router'
import { MapPin, Calendar, Wallet } from 'lucide-react'
import { Page, PageHeader, Card, SkeletonRows, EmptyState } from '@/components/rm'
import { fmtDate, fmtDateTime, phDay, pesoFromCentavos } from '@/lib/ph'
import { useCategories } from '@/lib/listing'
import { useTasksForCompany, type BusinessTask } from '@/lib/tasks'
import { cn } from '@/lib/utils'

// Spec 005. Open tasks in the places and categories this business's listing covers, and tasks it
// has already offered on. English first (business tools).
export const Route = createFileRoute('/_app/marketplace')({ component: TasksNearYou })

export const OFFER_LABEL: Record<string, string> = { active: 'Offer sent', withdrawn: 'Offer withdrawn', accepted: 'Offer accepted', declined: 'Not chosen' }

function TasksNearYou() {
  const tasks = useTasksForCompany()
  const cats = useCategories()
  const cat = (slug: string) => cats.data?.find((c) => c.slug === slug)?.name_en ?? slug
  const open = (tasks.data ?? []).filter((t) => t.status === 'open' && !t.my_offer)
  const mine = (tasks.data ?? []).filter((t) => t.my_offer)

  return (
    <Page>
      <PageHeader title="Tasks near you" subtitle="Clients in the places you serve who want the work you list. Make an offer." />
      <VerifyBanner what="offers" />
      {tasks.isLoading && <Card className="mt-5"><SkeletonRows /></Card>}
      {!tasks.isLoading && open.length === 0 && mine.length === 0 && (
        <Card className="mt-5"><EmptyState label="No open tasks in your area right now. Publish your listing and add the places you serve to see more." /></Card>
      )}
      {open.length > 0 && <TaskList title="Open tasks" tasks={open} cat={cat} />}
      {mine.length > 0 && <TaskList title="Your offers" tasks={mine} cat={cat} />}
    </Page>
  )
}

function TaskList({ title, tasks, cat }: { title: string; tasks: BusinessTask[]; cat: (s: string) => string }) {
  return (
    <section className="mt-5">
      <h2 className="mb-2 text-[12px] font-bold uppercase tracking-[0.06em] text-token-muted">{title}</h2>
      <div className="space-y-3">
        {tasks.map((t) => (
          <Link key={t.id} to="/marketplace/$id" params={{ id: t.id }} className="block rounded-[14px] border border-token-subtle bg-token-surface p-4 hover:border-token-strong">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-token-primary">{t.title}</p>
                <p className="mt-0.5 text-[12.5px] text-token-secondary">{cat(t.category_slug)} · {t.client_first_name} · posted {fmtDateTime(t.created_at)}</p>
              </div>
              {t.my_offer && <span className={cn('flex-none rounded-full px-2.5 py-0.5 text-[12px] font-semibold',
                t.my_offer.status === 'accepted' ? 'bg-token-success-surface text-token-success' : 'bg-token-hover text-token-secondary')}>{OFFER_LABEL[t.my_offer.status]}</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-token-secondary">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[t.barangay, t.city].filter(Boolean).join(', ')}</span>
              {t.budget_centavos != null && <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />Budget {pesoFromCentavos(t.budget_centavos)}</span>}
              {t.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(phDay(t.preferred_date))}</span>}
              <span>{t.offer_count} offer{t.offer_count === 1 ? '' : 's'}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  )
}
